import React from 'react'
import { useForm, Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import type { Credential, VirtualBucket, Backend, Account } from '@/types'

interface VirtualBucketWithBackend extends VirtualBucket {
  backend: Backend
}

interface Props {
  credential: Credential & { virtual_bucket_ids?: number[] }
  availableVirtualBuckets: VirtualBucketWithBackend[]
}

export default function CredentialsEdit({ credential, availableVirtualBuckets }: Props) {
  const { currentAccount } = usePage<{ currentAccount: Account }>().props

  const { data, setData, patch, processing, errors } = useForm({
    name: credential.name || '',
    description: credential.description || '',
    permissions: credential.permissions || 'read_write',
    virtual_bucket_ids: credential.virtual_bucket_ids || [],
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/accounts/${currentAccount.slug}/credentials/${credential.id}`)
  }

  const toggleVirtualBucket = (id: number) => {
    setData('virtual_bucket_ids',
      data.virtual_bucket_ids.includes(id)
        ? data.virtual_bucket_ids.filter(v => v !== id)
        : [...data.virtual_bucket_ids, id]
    )
  }

  // Group virtual buckets by backend
  const groupedVirtualBuckets = availableVirtualBuckets.reduce((acc, vb) => {
    const backendName = vb.backend?.name || 'Unknown'
    if (!acc[backendName]) acc[backendName] = []
    acc[backendName].push(vb)
    return acc
  }, {} as Record<string, VirtualBucketWithBackend[]>)

  return (
    <ManagementLayout>
      <Box sx={{ maxWidth: 672, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={600}>
            Edit Credential
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update {credential.name} settings.
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Name"
              fullWidth
              value={data.name}
              onChange={(e) => setData('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || 'A friendly name to identify this credential.'}
              placeholder="Production API Key"
              sx={{ mb: 3 }}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={data.description}
              onChange={(e) => setData('description', e.target.value)}
              error={!!errors.description}
              helperText={errors.description || 'Optional description for this credential.'}
              placeholder="Used by the production application server"
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Permissions</InputLabel>
              <Select
                value={data.permissions}
                label="Permissions"
                onChange={(e) => setData('permissions', e.target.value as 'read_only' | 'write_only' | 'read_write')}
              >
                <MenuItem value="read_write">Read & Write</MenuItem>
                <MenuItem value="read_only">Read Only</MenuItem>
                <MenuItem value="write_only">Write Only</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Assign Virtual Buckets
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Select which virtual buckets this credential can access.
              </Typography>
              {Object.keys(groupedVirtualBuckets).length > 0 ? (
                Object.entries(groupedVirtualBuckets).map(([backendName, vbs]) => (
                  <Paper key={backendName} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                      {backendName}
                    </Typography>
                    <FormGroup sx={{ ml: 1 }}>
                      {vbs.map((vb) => (
                        <FormControlLabel
                          key={vb.id}
                          control={
                            <Checkbox
                              checked={data.virtual_bucket_ids.includes(vb.id)}
                              onChange={() => toggleVirtualBucket(vb.id)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">{vb.name}</Typography>
                              {vb.base_path && (
                                <Typography variant="caption" color="text.secondary">
                                  {vb.base_path}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      ))}
                    </FormGroup>
                  </Paper>
                ))
              ) : (
                <Alert severity="warning">
                  No virtual buckets available. Create a backend and virtual buckets first.
                </Alert>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button component={Link} href={`/accounts/${currentAccount.slug}/credentials/${credential.id}`}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={processing}>
                Update Credential
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </ManagementLayout>
  )
}
